<template>
    <div class="user-page">
        <main class="dashboard">
            <!-- 头部：左上头像 + 右侧 ID/VIP，底部居中邀请码 + 右上语言切换 -->
            <header class="profile">
             

                <div class="profile-top">
                    <div class="avatar" aria-hidden="true">{{ avatarText }}</div>
                    <div class="id-block">
                        <b class="user-id">{{ displayId }}</b>
                        <span v-if="displayLevel" class="vip-badge"  @click="$router.push('/vip')">
                            {{ displayLevel }}
                            <i class="chev" aria-hidden="true">›</i>
                        </span>
                        <span class="invite-line">
                            {{ getLabel('user.invitationCode', 'Código de invitación') }}
                            <b class="invite-value">{{ inviteCode }}</b>
                            <button
                                type="button"
                                class="copy-code"
                                :aria-label="getLabel('user.copyInviteCode', 'Copiar código de invitación')"
                                @click="copyInviteCode"
                            >
                                <van-icon name="description" />
                            </button>
                        </span>
                    </div>
                    
                </div>

                
            </header>

            <!-- 资产数据：2 列 × 3 行，居中，行/列分隔线 -->
            <section class="assets-card">
                <div v-for="item in assetItems" :key="item.label" class="asset-cell">
                    <b class="asset-value">{{ item.value }}</b>
                    <span class="asset-label">{{ item.label }}</span>
                </div>
            </section>

            <!-- 主功能按钮：充值 / 提现 / 充值记录 / 提现记录 -->
            <nav class="actions" :aria-label="getLabel('user.functionNav', 'Funciones de cuenta')">
                <button
                    v-for="item in actionItems"
                    :key="item.label"
                    type="button"
                    class="action"
                    @click="handleFunctionClick(item)"
                >
                    <van-icon :name="item.icon" />
                    <span>{{ item.label }}</span>
                </button>
            </nav>

            <!-- 礼品兑换：左侧标题 + 右侧装饰头像/转盘 + 底部输入 -->
            <section class="redeem-card">
            
                <div class="redeem-head">
                    <div class="redeem-art" aria-hidden="true">
                        <div class="gift-box"></div>
                    </div>
                    <div class="redeem-copy">
                        <strong>{{ getLabel('user.redeemGiftTitle', 'Canjear Código de Regalo') }}</strong>
                        <p>{{ getLabel('user.redeemGiftSubtitle', 'Ingresa el código para reclamar recompensas') }}</p>
                    </div>
                </div>
                <div class="redeem-row">
                    <input
                        v-model.trim="redeemCode"
                        type="text"
                        class="redeem-input"
                        :placeholder="getLabel('user.redeemPlaceholder', 'INGRESA EL CÓDIGO DE REGALO')"
                        @keyup.enter="redeemGiftCode"
                    >
                    <button
                        type="button"
                        class="redeem-btn"
                        :disabled="redeeming"
                        @click="redeemGiftCode"
                    >
                        {{ getLabel('user.redeem', 'Reclamar') }}
                    </button>
                </div>
            </section>

            <!-- 功能模块：4 列 × 3 行 = 12 项 -->
            <nav class="menu-grid" :aria-label="getLabel('user.menuNav', 'Menú de cuenta')">
                <button
                    v-for="item in menuItems"
                    :key="item.label"
                    type="button"
                    class="menu-cell"
                    :class="{ 'menu-cell--logout': item.action === 'logout' }"
                    @click="handleFunctionClick(item)"
                >
                    <span v-if="item.decor" class="menu-decor" aria-hidden="true"></span>
                    <span class="menu-icon">
                        <van-icon :name="item.icon" />
                    </span>
                    <span class="menu-label">{{ item.label }}</span>
                </button>
            </nav>
        </main>

        <!-- 底部导航：首页 / 活动 / 团队 / 充值 / 账户 -->
        <nav class="bottom-nav">
            <router-link to="/index" class="nav-item">
                <van-icon name="home-o" />
                <span>{{ $t('home.index') }}</span>
            </router-link>
            <router-link to="/activity" class="nav-item">
                <van-icon name="star-o" />
                <span>{{ $t('home.activity') }}</span>
            </router-link>
            <router-link to="/myTeam" class="nav-item">
                <van-icon name="friends-o" />
                <span>{{ $t('new_lang.team') }}</span>
            </router-link>
            <router-link to="/invest" class="nav-item">
                <van-icon name="balance-o" />
                <span>{{ getLabel('user.recharge', 'Recargar') }}</span>
            </router-link>
            <router-link to="/user" class="nav-item active">
                <van-icon name="contact" />
                <span>{{ $t('home.my') }}</span>
            </router-link>
        </nav>

        <button
            type="button"
            class="service-float"
            :aria-label="getLabel('user.onlineService', 'Servicio en línea')"
            @click="$router.push('/kefu')"
        >
            <van-icon name="service-o" />
        </button>
    </div>
</template>

<script>
import Vue from "vue";
import Fetch from "../../utils/fetch";
import { Icon, Toast } from "vant";

Vue.use(Icon);
Vue.use(Toast);

// 礼品码兑换接口：后端确认后如与约定不一致，请同步修改此处
const REDEEM_API = "/user/redeem";

export default {
    name: "user",
    data() {
        return {
            data: {},
            teamData: {},
            redeemCode: "",
            redeeming: false,
            appOriginalStyles: null,
            bodyOriginalBackground: "",
        };
    },
    computed: {
        langCode() {
            const lang = String(localStorage.getItem("lang") || "spanish").toLowerCase();
            const map = {
                spanish: "Esp.",
                en_us: "EN",
                zh_cn: "ZH",
                zh_hk: "ZH",
                ja_jp: "JA",
                ko_kr: "KO",
                ms_my: "MS",
                th_th: "TH",
                vi_vn: "VI",
            };
            return map[lang] || "ES";
        },
        displayName() {
            return this.data.name || this.data.mobile || this.data.username || this.getLabel("user.userNameFallback", "Usuario");
        },
        displayLevel() {
            return this.data.vip_name || this.data.vip_name || "VIP0";
        },
        displayId() {
            // 顶部大字显示账户号（登录账号），邀请码仍为用户 ID
            return this.data.username || this.data.account || this.data.user_name || this.data.account_no || this.data.mobile || this.data.uid || this.data.user_id || "--";
        },
        avatarText() {
            const source = String(this.displayName || "0");
            return source.slice(0, 1).toUpperCase();
        },
        inviteCode() {
            // 实际邀请码就是用户 ID（非手机号）：优先取用户 ID，缺失时才回退其他来源
            const uid = this.data.id || this.data.uid || this.data.user_id;
            if (uid) {
                return String(uid);
            }
            const directCode = this.teamData.code || this.data.code || this.data.invite_code || this.data.share_code;
            if (directCode) {
                return directCode;
            }
            return this.extractInviteCode(this.teamData.share_link) || "--";
        },
        inviteLink() {
            if (this.teamData.share_link) {
                return this.teamData.share_link;
            }
            const origin = window.location.origin + window.location.pathname.replace(/\/$/, "");
            return origin.replace(/#.*$/, "") + "#/register?invite=" + encodeURIComponent(this.inviteCode || "");
        },
        assetItems() {
            return [
                {
                    label: this.getLabel("user.saldoTotal", "Saldo total"),
                    value: this.formatCurrency(this.data.money),
                },
                {
                    label: this.getLabel("user.totalIncome", "Ingresos Totales"),
                    value: this.formatCurrency(this.data.all_lixi),
                },
                {
                    label: this.getLabel("user.rechargeAccount", "Cuenta de Recarga"),
                    value: this.formatCurrency(this.data.recharge_money || this.data.recharge || 0),
                },
                {
                    label: this.getLabel("user.rewardAccount", "Cuenta de Recompensas"),
                    value: this.formatCurrency(this.teamData.countCommission || this.data.reward_money || this.data.reward || 0),
                },
                {
                    label: this.getLabel("user.totalWithdraw", "Retiros Totales"),
                    value: this.formatCurrency(this.data.withdraw_money || this.data.withdraw || 0),
                },
                {
                    label: this.getLabel("user.todayIncome", "Ingresos de Hoy"),
                    value: this.formatCurrency(this.data.today_income || this.data.today_lixi || 0),
                },
            ];
        },
        actionItems() {
            return [
                { label: this.getLabel("user.recharge", "Recargar"), icon: "balance-o", path: "/invest?tabActive=0" },
                { label: this.getLabel("user.withdraw", "Retirar"), icon: "cash-back-record", path: "/invest?tabActive=1" },
                { label: this.getLabel("user.rechargeRecord", "Registro de recarga"), icon: "clock-o", path: "/rechargeRecord" },
                { label: this.getLabel("user.withdrawalRecord", "Registro de retiro"), icon: "clock-o", path: "/withdrawRecord" },
            ];
        },
        menuItems() {
            return [
                { label: this.getLabel("user.rentOrders", "Órdenes de alquiler"), icon: "notes-o", path: "/order" },
                { label: this.getLabel("user.earnings", "Ganancias"), icon: "chart-trending-o", path: "/moneybag" },
                { label: this.getLabel("user.bankCards", "Tarjetas bancarias"), icon: "bank-card-o", path: "/account" },
                { label: this.getLabel("user.teamReport", "Informe de equipo"), icon: "friends-o", path: "/myTeam", decor: true },
                { label: this.getLabel("user.inviteFriends", "Invitar amigos"), icon: "user-o", action: "invite" },
                { label: this.getLabel("user.treasureChest", "Cofre del Tesoro"), icon: "gift-o", action: "soon" },
                { label: this.getLabel("user.fundsPassword", "Contraseña de Fondos"), icon: "shield-o", path: "/setpaypwd" },
                { label: this.getLabel("user.channel", "Canal"), icon: "chat-o", path: "/kefu" },
                { label: this.getLabel("user.notifications", "Notificaciones"), icon: "bell", action: "soon" },
                { label: this.getLabel("user.lottery", "Sorteo"), icon: "award-o", path: "/luckDraw" },
                { label: this.getLabel("user.downloadApp", "Descargar APP"), icon: "down", action: "download", url: "https://art.innovativeaitechnology.com/INNOVA AI_1_1.0.apk" },
                { label: this.getLabel("user.changePassword", "Cambiar Contraseña"), icon: "edit", path: "/setpwd" },
                { label: this.getLabel("safe.signOut", "Cerrar sesión"), icon: "revoke", action: "logout" },
            ];
        },
    },
    created() {
        this.$parent.footer("user", false);
    },
    mounted() {
        this.applyFullScreenLayout();
        this.start();
    },
    beforeDestroy() {
        this.restoreFullScreenLayout();
    },
    methods: {
        getLabel(key, defaultText) {
            return this.$te(key) ? this.$t(key) : defaultText;
        },
        applyFullScreenLayout() {
            const app = document.getElementById("app");
            if (!app) {
                return;
            }
            this.appOriginalStyles = {
                maxWidth: app.style.maxWidth,
                width: app.style.width,
                margin: app.style.margin,
                backgroundColor: app.style.backgroundColor,
                paddingBottom: app.style.paddingBottom,
            };
            this.bodyOriginalBackground = document.body.style.background;
            app.style.maxWidth = "100%";
            app.style.width = "100%";
            app.style.margin = "0";
            app.style.backgroundColor = "#05070f";
            app.style.paddingBottom = "0";
            document.body.style.background = "#05070f";
        },
        restoreFullScreenLayout() {
            const app = document.getElementById("app");
            if (app && this.appOriginalStyles) {
                app.style.maxWidth = this.appOriginalStyles.maxWidth || "";
                app.style.width = this.appOriginalStyles.width || "";
                app.style.margin = this.appOriginalStyles.margin || "";
                app.style.backgroundColor = this.appOriginalStyles.backgroundColor || "";
                app.style.paddingBottom = this.appOriginalStyles.paddingBottom || "";
            }
            document.body.style.background = this.bodyOriginalBackground || "";
        },
        start() {
            Fetch("/user/info").then((r) => {
                this.data = r.data || {};
            });
            Fetch("/user/myTeam").then((r) => {
                this.teamData = r.data || {};
            }).catch(() => {});
        },
        handleFunctionClick(item) {
            if (item.action === "invite") {
                this.copyInviteLink();
                return;
            }
            if (item.action === "logout") {
                this.logout();
                return;
            }
            if (item.action === "download") {
                const ua = String(navigator.userAgent || "").toLowerCase();
                const isAndroid = /android/i.test(ua);
                if (isAndroid && item.url) {
                    window.location.href = item.url;
                } else {
                    Toast(this.getLabel("user.comingSoon", "Próximamente"));
                }
                return;
            }
            if (item.action === "soon") {
                Toast(this.getLabel("user.comingSoon", "Próximamente"));
                return;
            }
            if (!item.path) {
                return;
            }
            this.$router.push(item.path);
        },
        logout() {
            // 与 safe.vue 的登出逻辑保持一致：清除本地登录态后回到登录页
            localStorage.removeItem("token");
            localStorage.removeItem("ssid");
            this.$router.replace("/login");
        },
        redeemGiftCode() {
            const code = String(this.redeemCode || "").trim();
            if (!code) {
                Toast(this.getLabel("user.redeemEmpty", "Ingresa un código de regalo"));
                return;
            }
            if (this.redeeming) {
                return;
            }
            this.redeeming = true;
            Fetch(REDEEM_API, { code }).then(() => {
                this.redeemCode = "";
                Toast(this.getLabel("user.redeemSuccess", "¡Recompensa reclamada con éxito!"));
                this.start();
            }).catch(() => {
                // 错误提示由全局 Fetch 统一弹出
            }).then(() => {
                this.redeeming = false;
            });
        },
        formatCurrency(value) {
            return "MXN" + Number(value || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        extractInviteCode(link) {
            if (!link) {
                return "";
            }
            try {
                const parsed = new URL(link, window.location.origin);
                const keys = ["invite", "m", "code", "agent"];
                for (const key of keys) {
                    const value = parsed.searchParams.get(key);
                    if (value) {
                        return value;
                    }
                }
                const hashQuery = parsed.hash.split("?")[1];
                if (hashQuery) {
                    const hashParams = new URLSearchParams(hashQuery);
                    for (const key of keys) {
                        const value = hashParams.get(key);
                        if (value) {
                            return value;
                        }
                    }
                }
            } catch (error) {
                const match = String(link).match(/(?:invite|m|code|agent)=([^&#]+)/i);
                return match ? decodeURIComponent(match[1]) : "";
            }
            return "";
        },
        copyText(text) {
            const value = String(text || "");
            if (!value || value === "--") {
                Toast(this.getLabel("user.noCopyContent", "Nada que copiar"));
                return;
            }
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(value).then(() => {
                    Toast(this.getLabel("invest_qrcode.copySucceeded", "Copiado correctamente"));
                }).catch(() => {
                    this.fallbackCopy(value);
                });
                return;
            }
            this.fallbackCopy(value);
        },
        fallbackCopy(text) {
            const input = document.createElement("textarea");
            input.value = text;
            input.setAttribute("readonly", "");
            input.style.position = "fixed";
            input.style.opacity = "0";
            document.body.appendChild(input);
            input.select();
            input.setSelectionRange(0, input.value.length);
            try {
                document.execCommand("copy");
                Toast(this.getLabel("invest_qrcode.copySucceeded", "Copiado correctamente"));
            } catch (error) {
                Toast(this.getLabel("user.copyFailed", "Error al copiar, copia manualmente"));
            }
            document.body.removeChild(input);
        },
        copyInviteCode() {
            this.copyText(this.inviteCode);
        },
        copyInviteLink() {
            this.copyText(this.inviteLink);
        },
    },
};
</script>

<style lang="less" scoped>
.user-page {
    min-height: 100vh;
    background: #05070f;
    color: #fff;
}

.dashboard {
    width: 100%;
    max-width: 430px;
    min-height: 100vh;
    margin: 0 auto;
    padding: 0 12px 118px;
    background: #05070f;
    box-sizing: border-box;
}

/* ---------- 头部 ---------- */
.profile {
    position: relative;
    border-bottom-left-radius: 10px;
    border-bottom-right-radius: 10px;
    height: 130px;
    margin: 0 -12px 14px;
    padding: 0;
    overflow: hidden;
    background:
        linear-gradient(90deg, rgba(0, 0, 0, 0.32), transparent 64%),
        url("https://6584545.it.com/assets/index/img/dashboard-earth.jpg"),
        radial-gradient(ellipse at 82% 115%, #0952d7 0, rgba(5, 52, 155, 0.78) 22%, rgba(0, 16, 62, 0.32) 48%, transparent 68%),
        linear-gradient(120deg, #01040a 15%, #02091a 62%, #031540);
    background-size: cover, 94% auto, auto, auto;
    background-position: center, right 70px, center, center;
    background-repeat: no-repeat;
}

.lang-btn {
    position: absolute;
    top: 14px;
    right: 16px;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: 4px;
    height: 28px;
    padding: 0 10px;
    border: 1px solid rgba(120, 165, 255, 0.35);
    border-radius: 14px;
    background: rgba(3, 10, 26, 0.55);
    color: #cfe0ff;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
}

.lang-btn svg {
    width: 14px;
    height: 14px;
}

.profile-top {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 22px 20px 0;
}

.avatar {
    margin-top: 10px;
    flex: 0 0 70px;
    width: 70px;
    height: 70px;
    border-radius: 50%;
    border: 2px solid rgba(120, 180, 255, 0.45);
    background: radial-gradient(circle at 35% 30%, #7ec8ff, #1a4fc4 48%, #0a1a4a 82%);
    box-shadow: 0 0 18px rgba(80, 140, 255, 0.55), inset 0 0 8px rgba(180, 210, 255, 0.4);
    display: grid;
    place-items: center;
    font-size: 24px;
    font-weight: 800;
    color: #fff;
}

.id-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-top: 4px;
    min-width: 0;
}

.user-id {
    min-width: 0;
    font-size: 26px;
    font-weight: 800;
    letter-spacing: 1px;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background: linear-gradient(180deg, #ffffff, #9ec5ff);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    font-variant-numeric: tabular-nums;
}

.vip-badge {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 10px;
    background: rgba(40, 90, 220, 0.4);
    border: 1px solid rgba(90, 140, 255, 0.5);
    color: #8fb8ff;
    font-size: 11px;
    font-weight: 600;
}

.vip-badge .chev {
    font-style: normal;
    font-size: 13px;
    line-height: 1;
}

.invite-line {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 2px;
    color: #a7afbf;
    font-size: 13px;
}

.invite-value {
    font-family: "Arial Black", Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #4d8dff;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
}

.copy-code {
    flex: 0 0 22px;
    width: 22px;
    height: 22px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #3978ff;
    display: grid;
    place-items: center;
    cursor: pointer;
}

.copy-code .van-icon {
    font-size: 15px;
}

/* ---------- 资产数据 ---------- */
.assets-card {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-bottom: 14px;
    border-radius: 16px;
    background: linear-gradient(180deg, rgba(13, 24, 54, 0.9), rgba(4, 9, 24, 0.9));
    border: 1px solid rgba(20, 52, 112, 0.26);
    overflow: hidden;
}

.asset-cell {
    min-width: 0;
    padding: 16px 10px;
    text-align: center;
}

.asset-cell:nth-child(even) {
    border-left: 1px solid rgba(255, 255, 255, 0.06);
}

.asset-cell:nth-child(n + 3) {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.asset-value {
    display: block;
    margin-bottom: 6px;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.25;
    color: #fff;
    letter-spacing: -0.2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-variant-numeric: tabular-nums;
}

.asset-label {
    display: block;
    font-size: 12px;
    line-height: 1.4;
    color: #8f9aaf;
}

/* ---------- 主功能按钮 ---------- */
.actions {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 14px;
}

.action {
    height: 76px;
    padding: 8px 4px;
    border: 1px solid rgba(29, 76, 181, 0.35);
    border-radius: 14px;
    background: #071228;
    color: #f2f5fb;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 0;
    cursor: pointer;
}

.action .van-icon {
    font-size: 26px;
    color: #dce8ff;
}

.action span {
    max-width: 100%;
    font-size: 12px;
    line-height: 1.3;
    text-align: center;
    white-space: normal;
}

/* ---------- 礼品兑换 ---------- */
.redeem-card {
    position: relative;
    padding: 14px;
    margin-bottom: 14px;
    border-radius: 16px;
    background:
        radial-gradient(circle at 92% 20%, rgba(125, 75, 255, 0.16), transparent 46%),
        #020b1e;
    border: 1px solid rgba(20, 52, 112, 0.26);
    overflow: hidden;
}

.redeem-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
    padding-right: 80px;
}

.redeem-art {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(47, 99, 245, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.gift-box {
    position: relative;
    width: 22px;
    height: 19px;
    border-radius: 4px;
    background: linear-gradient(145deg, #7d4bff, #451aff);
    box-shadow: 0 0 14px rgba(125, 75, 255, 0.5);
}

.gift-box::before,
.gift-box::after {
    content: "";
    position: absolute;
    background: rgba(220, 202, 255, 0.95);
}

.gift-box::before {
    left: 50%;
    top: 0;
    width: 4px;
    height: 100%;
    transform: translateX(-50%);
}

.gift-box::after {
    left: 0;
    top: 50%;
    width: 100%;
    height: 4px;
    transform: translateY(-50%);
}

.redeem-copy {
    min-width: 0;
}

.redeem-copy strong {
    display: block;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 4px;
}

.redeem-copy p {
    margin: 0;
    color: #8f9aaf;
    font-size: 12px;
    line-height: 1.5;
}

.redeem-avatar {
    position: absolute;
    top: 10px;
    right: 12px;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 2px solid rgba(255, 255, 255, 0.2);
    background: radial-gradient(circle at 35% 30%, #ffd0a0, #c45a8a 50%, #4a1a4a);
}

.redeem-wheel {
    position: absolute;
    right: 84px;
    top: 44px;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: conic-gradient(
        #ffd25e 0 30deg, #ff6b6b 30deg 60deg,
        #4ecdc4 60deg 90deg, #6c5ce7 90deg 120deg,
        #ffd25e 120deg 150deg, #ff6b6b 150deg 180deg,
        #4ecdc4 180deg 210deg, #6c5ce7 210deg 240deg,
        #ffd25e 240deg 270deg, #ff6b6b 270deg 300deg,
        #4ecdc4 300deg 330deg, #6c5ce7 330deg 360deg
    );
    box-shadow: 0 0 14px rgba(108, 92, 231, 0.5);
}

.redeem-wheel::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 12px;
    height: 12px;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background: #fff;
}

.redeem-row {
    display: flex;
    gap: 10px;
}

.redeem-input {
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 14px;
    border: 1px solid rgba(47, 99, 245, 0.4);
    border-radius: 10px;
    background: #030f27;
    color: #fff;
    font-size: 13px;
    letter-spacing: 1px;
    outline: none;
    box-sizing: border-box;
}

.redeem-input::placeholder {
    color: #5a6a8c;
    letter-spacing: 0.5px;
}

.redeem-btn {
    flex: 0 0 auto;
    height: 44px;
    padding: 0 22px;
    border: 0;
    border-radius: 10px;
    background: linear-gradient(135deg, #2f63f5, #5939f3);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
}

.redeem-btn[disabled] {
    opacity: 0.6;
    cursor: not-allowed;
}

/* ---------- 功能模块 4×3 ---------- */
.menu-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px 8px;
}

.menu-cell {
    position: relative;
    height: 86px;
    padding: 10px 4px;
    border: 1px solid rgba(29, 76, 181, 0.35);
    border-radius: 14px;
    background: #071228;
    color: #f2f5fb;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 0;
    cursor: pointer;
    overflow: hidden;
}

.menu-icon {
    color: #b8ccff;
    display: flex;
    align-items: center;
    justify-content: center;
}

.menu-icon .van-icon {
    font-size: 26px;
}

.menu-label {
    max-width: 100%;
    font-size: 11px;
    line-height: 1.3;
    text-align: center;
    white-space: normal;
}

.menu-decor {
    position: absolute;
    right: -4px;
    bottom: -6px;
    width: 38px;
    height: 30px;
    border-radius: 50% 50% 0 0;
    background: radial-gradient(circle at 50% 100%, #ffd25e, #b8860b 60%, transparent 75%);
    opacity: 0.85;
    pointer-events: none;
}

.menu-cell--logout {
    border-color: rgba(255, 90, 90, 0.4);
}

.menu-cell--logout .menu-icon .van-icon {
    color: #ff8080;
}

.menu-cell--logout .menu-label {
    color: #ff9a9a;
}

/* ---------- 底部导航 ---------- */
.bottom-nav {
    position: fixed;
    left: 50%;
    bottom: 0;
    z-index: 50;
    width: 100%;
    max-width: 430px;
    padding: 8px 6px max(8px, env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(0, 0, 0, 0.92);
    backdrop-filter: blur(16px);
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: space-around;
    box-sizing: border-box;
}

.nav-item {
    flex: 1;
    min-width: 0;
    padding: 6px 4px;
    color: #6b7280;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.nav-item .van-icon {
    font-size: 22px;
}

.nav-item span {
    margin-top: 4px;
    font-size: 11px;
    line-height: 1.25;
    font-weight: 500;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.nav-item.active,
.nav-item.router-link-exact-active {
    color: #2d5bff;
}

.nav-item.active span,
.nav-item.router-link-exact-active span {
    font-weight: 700;
}

/* ---------- 在线客服悬浮按钮 ---------- */
.service-float {
    position: fixed;
    right: max(18px, calc((100vw - 430px) / 2 + 18px));
    bottom: max(86px, calc(env(safe-area-inset-bottom) + 86px));
    z-index: 80;
    width: 54px;
    height: 54px;
    border: 1px solid rgba(156, 190, 255, 0.58);
    border-radius: 50%;
    background: linear-gradient(145deg, #337cff, #5939f3);
    color: #fff;
    box-shadow: 0 0 0 6px rgba(45, 91, 255, 0.1), 0 9px 24px rgba(21, 75, 255, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.service-float .van-icon {
    font-size: 28px;
}

@media (max-width: 360px) {
    .dashboard {
        padding-left: 9px;
        padding-right: 9px;
    }

    .profile {
        margin-left: -9px;
        margin-right: -9px;
        height: 120px;
    }

    .profile-top {
        padding: 18px 16px 0;
    }

    .avatar {
        flex-basis: 54px;
        width: 54px;
        height: 54px;
    }

    .user-id {
        font-size: 23px;
    }

    .asset-cell {
        padding: 13px 6px;
    }

    .asset-value {
        font-size: 17px;
    }

    .actions {
        gap: 5px;
    }

    .action {
        height: 72px;
    }

    .action span {
        font-size: 11px;
    }

    .menu-grid {
        gap: 7px 5px;
    }

    .menu-cell {
        height: 80px;
    }

    .menu-label {
        font-size: 10px;
    }

    .redeem-wheel {
        right: 76px;
        width: 54px;
        height: 54px;
    }

    .redeem-head {
        padding-right: 72px;
    }
}
</style>
